import React from 'react';
import {
  ChannelLoyalty,
  ORDER_CHALLENGE,
} from 'js/model/rainbow/content/ChannelOutput';
import { formatPrice } from 'js/helpers/price/priceUtilities';
import { substituteWithDictionary } from 'js/helpers/i18n';
import { CMSTreatwellRewardsPage } from 'js/model/cms/cms-treatwell-rewards';
import { useTranslation } from 'react-i18next';
import { LoyaltyContainer } from '../LoyaltyContainer';
import { InstructionSteps, InstructionStepsProps } from './InstructionSteps';
import styles from './InstructionStepsSection.module.css';
import { SectionHeader } from '../SectionHeader';
import { getAchievements } from '../utils/getAchievements';

type InstructionStepsSectionProps = {
  title: string;
  description: string;
} & InstructionStepsProps;

const InstructionStepsSection = ({
  title,
  description,
  book,
  earn,
  redeem,
}: InstructionStepsSectionProps) => {
  return (
    <section className={styles.root}>
      <LoyaltyContainer>
        <SectionHeader title={title} description={description} />
        <div className={styles.instructionSteps}>
          <InstructionSteps book={book} earn={earn} redeem={redeem} />
        </div>
      </LoyaltyContainer>
    </section>
  );
};

type Props = {
  channel: ChannelLoyalty;
  cmsValues: CMSTreatwellRewardsPage;
};

export const InstructionStepsSectionContainer = ({
  channel,
  cmsValues,
}: Props) => {
  const { earnRateNumerator, earnRateDenominator } =
    channel.loyalty.challenges[ORDER_CHALLENGE];
  const earnRate = formatPrice(earnRateNumerator, channel);
  const achievements = channel.loyalty.achievements;
  const { lowestAchievementValue, highestAchievementValue } = getAchievements({
    achievements,
    channel,
  });
  const cms = cmsValues.page['treatwell-rewards']['how-it-works'];
  const { t } = useTranslation();
  const title = t('treatwellRewards.howItWorks.title');
  const description = cms.description;
  const book = {
    label: cms.steps.book.title,
    description: cms.steps.book.description,
  };
  const earnDescription = substituteWithDictionary(cms.steps.earn.description, {
    points: String(earnRateDenominator),
    earnRate,
  });
  const earn = {
    label: cms.steps.earn.title,
    description: earnDescription,
  };
  const redeemDescription = substituteWithDictionary(
    cms.steps.redeem.description,
    { lowestAchievementValue, highestAchievementValue }
  );
  const redeem = {
    label: cms.steps.redeem.title,
    description: redeemDescription,
  };
  return (
    <InstructionStepsSection
      title={title}
      description={description}
      book={book}
      earn={earn}
      redeem={redeem}
    />
  );
};
